import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
} from "@angular/core";
import { filter, map } from "rxjs";

import { Settings } from "@hermes/app-core";
import {
    GridCategory,
    GridFacade,
    GridFilter,
    GridFilterGroup,
    MIN_HIGHLIGHTS_LENGTH,
} from "@hermes/states/grid-filters-state";
import { SCENE7_IMAGE_BACKGROUND_COLOR } from "@hermes/utils-generic/constants";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";

const SCENE7_GRIDHIGHLIGHT_DEFAULT_IMAGE = "gridhighlight_default";
const ARIA_LABEL_ITEM = $localize`:@@hermes.accessibility.item:item`;
const ARIA_LABEL_ITEMS = $localize`:@@hermes.accessibility.items:items`;

/**
 * This component is used to display each highlight (shortcut that contains a link to a filtered grid) on the grid page
 */
@Component({
    selector: "h-grid-highlight-item",
    templateUrl: "./grid-highlight-item.component.html",
    styleUrls: ["./grid-highlight-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridHighlightItemComponent implements OnChanges {
    @Input()
    public highlightedItemFilter!: GridFilter;

    @Input()
    public highlightedItemFilterGroup!: GridFilterGroup;

    @Input()
    public highlightItemIndex!: number;

    @Output()
    public highlightFocus: EventEmitter<void> = new EventEmitter<void>();

    public minHighlightLength = MIN_HIGHLIGHTS_LENGTH;

    public currentCategory!: string;

    public name!: string;

    public assetUrl$ = this.gridFacade.category$.pipe(
        filter(Boolean),
        map((category: GridCategory) => this.buildAssetUrl(category)),
    );

    constructor(
        private gridFacade: GridFacade,
        private settings: Settings,
        private cd: ChangeDetectorRef,
    ) {}

    public ngOnChanges(): void {
        if (
            this.highlightedItemFilter?.facetIsCrawlable &&
            this.highlightedItemFilterGroup
        ) {
            this.highlightedItemFilter.filterGroupName =
                this.highlightedItemFilterGroup.facetUrlParameterName;
        }
        this.cd.markForCheck();
    }

    /**
     * Link with fragment
     *
     * @returns urlParams or undefined
     */
    public getFragment(): string | undefined {
        return this.highlightedItemFilter.facetIsCrawlable
            ? undefined
            : this.highlightedItemFilter.urlParams;
    }

    /**
     * Link with query params
     *
     * @returns queryParams or empty
     */
    public getQueryParams(): Record<string, string> {
        const queryParameters: Record<string, string> = {};

        if (this.highlightedItemFilter.facetIsCrawlable) {
            queryParameters[this.highlightedItemFilter.filterGroupName] =
                this.highlightedItemFilter.value;
        }

        return queryParameters;
    }

    public onItemClick(event: Event): void {
        // Prevent initial href's behaviour to update the filter with initial current method
        // Keep href link for crawler and SEO
        stopEventPropagation(event);

        this.highlightedItemFilter.selected = true;
        this.gridFacade.updateGridFilter(
            this.highlightedItemFilter,
            this.highlightedItemFilterGroup,
            true,
        );
    }
    public getAriaLabel(): string {
        if (this.highlightedItemFilter.total > 1) {
            return `${this.highlightedItemFilter.name}, ${this.highlightedItemFilter.total} ${ARIA_LABEL_ITEMS}`;
        }
        return `${this.highlightedItemFilter.name}, ${this.highlightedItemFilter.total} ${ARIA_LABEL_ITEM}`;
    }

    private buildAssetUrl(category: GridCategory): string {
        const base = this.settings.assetsUrlProduct;

        // Combined filter
        if (this.highlightedItemFilter.combinedFilterImage) {
            return `${base}${this.highlightedItemFilter.combinedFilterImage}?defaultimage=${SCENE7_GRIDHIGHLIGHT_DEFAULT_IMAGE}&${SCENE7_IMAGE_BACKGROUND_COLOR}`;
        }

        // Simple filter
        const simpleFilterPath = `${category.pimCode.toLowerCase()}_${this.highlightedItemFilter.value.toLowerCase()}`;
        return `${base}${simpleFilterPath}?defaultimage=${SCENE7_GRIDHIGHLIGHT_DEFAULT_IMAGE}&${SCENE7_IMAGE_BACKGROUND_COLOR}`;
    }
}
